import React, { useEffect, useMemo, useState } from 'react';
import { usePolicyPageContext } from '../../PolicyPage/context/PolicyPageContext';
import { Grid } from '../../../ui';
import { currencyFormatter } from '../../QuotePage/content/QuoteGrids/utils';
import { useMCEPageContext } from '../context/MCEPageContext';

export const LocationDetails = React.memo((props) => {
  const { setGridReference } = props;
  const { policy } = usePolicyPageContext();
  const { MCEDetails, isEditing, setMCEDetails } = useMCEPageContext();
  const [locationsData, setLocationsData] = useState([]);
  const hailCoverageId = 9;

  const policyLocations = policy?.locations || [];
  const mceLocations = MCEDetails?.locations || [];
  const sourceLocations = useMemo(() => {
    return mceLocations.length ? mceLocations : policyLocations;
  }, [mceLocations, policyLocations]);
  const setHailDeductibleOverwrite = (locationId, enabled, deductibleValue) => {
    setMCEDetails((prev) => {
      const overwrites = prev?.overwrites ? [...prev.overwrites] : [];
      const remaining = overwrites.filter(
        (overwrite) => !(overwrite?.type === 'locationDeductible' && overwrite?.locationId === locationId && overwrite?.coverageId === hailCoverageId)
      );
      if (enabled) {
        return {
          ...prev,
          overwrites: [...remaining, { type: 'locationDeductible', locationId, coverageId: hailCoverageId }],
        };
      }
      const updatedLocations =
        prev?.locations?.map((location) => {
          if (location.id !== locationId) {
            return location;
          }
          const updatedCoverages = (location.coverages || []).map((coverage) => {
            if (coverage.coverageId === hailCoverageId) {
              return { ...coverage, deductible: deductibleValue };
            }
            return coverage;
          });
          return { ...location, coverages: updatedCoverages };
        }) || [];
      return { ...prev, overwrites: remaining, locations: updatedLocations };
    });
  };

  const columnDefs = [
    {
      headerName: 'RiskId',
      field: 'riskId',
      minWidth: 100,
    },
    {
      headerName: 'Name',
      field: 'name',
      minWidth: 150,
    },
    {
      headerName: 'Address',
      field: 'address',
      minWidth: 150,
    },
    {
      headerName: 'Average Declared Inventory',
      field: 'avg_declared_inventory',
      minWidth: 150,
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
      dataType: 'number',
    },
    {
      headerName: `Comp Payout Limit`,
      field: 'compPayoutLimit',
      dataType: 'number',
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: 'Flood Zone',
      field: 'details.femaZone',
      cellClassRules: {
        'red-background': (params) => params.data?.details?.isFloodZone,
      },
      minWidth: 150,
    },
    {
      headerName: 'Flood Sublimit',
      field: 'floodSublimit',
      valueFormatter: (params) => {
        if (params.value === null) {
          return '';
        }

        if (params.value === 'Full Sublimit') {
          return params.value;
        }

        return currencyFormatter({ field: { value: params.value || 0 } });
      },
      minWidth: 160,
    },
    {
      headerName: 'Earthquake Sublimit',
      field: 'earthquakeSublimit',
      valueFormatter: (params) => {
        if (params.value === null) {
          return '';
        }

        if (params.value === 'Full Sublimit') {
          return params.value;
        }

        return currencyFormatter({ field: { value: params.value || 0 } });
      },
      minWidth: 160,
    },
    {
      headerName: 'Distance from Coast',
      field: 'coastDistance',
      minWidth: 160,
    },
    {
      headerName: 'SWAP Needed',
      field: 'swapNeeded',
      cellRenderer: 'agCheckboxCellRenderer',
      minWidth: 130,
    },
    {
      headerName: `Flood Payout Limit`,
      field: 'floodPayoutLimit',
      dataType: 'number',
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: `EQ Payout Limit`,
      field: 'eqPayoutLimit',
      dataType: 'number',
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: `Hail Payout Limit`,
      field: 'hailPayoutLimit',
      dataType: 'number',
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
    {
      headerName: 'Override Hail Deductible',
      field: 'overrideHailDeductible',
      minWidth: 180,
      cellRenderer: 'agCheckboxCellRenderer',
      editable: (params) => isEditing && !!params.data?.locationId,
      valueSetter: (params) => {
        const enabled = !!params.newValue;
        params.data.overrideHailDeductible = enabled;
        if (!enabled) {
          params.data.hailDeductible = params.data.originalHailDeductible;
        }
        setHailDeductibleOverwrite(params.data.locationId, enabled, params.data.originalHailDeductible);
        return true;
      },
    },
    {
      headerName: 'Hail Deductible',
      field: 'hailDeductible',
      minWidth: 150,
      dataType: 'number',
      editable: (params) => isEditing && params.data?.overrideHailDeductible,
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
      cellClassRules: {
        'ag-cell-editable': (params) => isEditing && params.data?.overrideHailDeductible,
      },
      valueSetter: (params) => {
        const newValue = Number(params.newValue);
        params.data.hailDeductible = newValue;
        setMCEDetails((prev) => {
          const updatedLocations =
            prev?.locations?.map((location) => {
              if (location.id !== params.data.locationId) {
                return location;
              }
              const updatedCoverages = (location.coverages || []).map((coverage) => {
                if (coverage.coverageId === hailCoverageId) {
                  return { ...coverage, deductible: newValue };
                }
                return coverage;
              });
              return { ...location, coverages: updatedCoverages };
            }) || [];
          return { ...prev, locations: updatedLocations };
        });
        return true;
      },
    },
    {
      headerName: `Collison Payout Limit`,
      field: 'collisionPayoutLimit',
      dataType: 'number',
      valueFormatter: (field) => currencyFormatter({ field, withDecimals: true }),
    },
  ];

  const mapRowData = (locations) => {
    if (!locations) return [];

    const getPayoutLimit = (coverages, coverageType) => {
      const coverage = coverages && coverages.length && coverages.find((each) => each.coverageType === coverageType);
      if (coverage) {
        return coverage.payoutLimit;
      }
      return 0;
    };

    const getHailDeductible = (coverages) => {
      const coverage = coverages && coverages.length && coverages.find((each) => each.coverageId === hailCoverageId || each.coverageType === 'hail');
      return coverage?.deductible || 0;
    };

    const baseRowData =
      locations?.map((location) => {
        const locationId = location?.id || location?.locationId;
        const riskId = location?.riskId ?? location?.risk_id;
        const matchingCalculation = MCEDetails?.calculations?.find((loc) => loc.locationId === locationId);
        const { declaredInventory } = matchingCalculation || {};

        const coverages = location.coverages || [];
        const payoutLimits = {
          compPayoutLimit: getPayoutLimit(coverages, 'comprehensive'),
          floodPayoutLimit: getPayoutLimit(coverages, 'flood'),
          eqPayoutLimit: getPayoutLimit(coverages, 'earthquake'),
          hailPayoutLimit: getPayoutLimit(coverages, 'hail'),
          collisionPayoutLimit: getPayoutLimit(coverages, 'collision'),
        };
        const hailDeductible = getHailDeductible(coverages);
        const hailDeductibleOverride = MCEDetails?.overwrites?.some(
          (overwrite) => overwrite?.type === 'locationDeductible' && overwrite?.locationId === locationId && overwrite?.coverageId === hailCoverageId
        );

        let floodSublimit = null;
        if (location.requirements && Object.keys(location.requirements).length > 0) {
          if (!location.requirements.hasOwnProperty('floodSublimit') || location.requirements.floodSublimit === null) {
            floodSublimit = 'Full Sublimit';
          } else {
            floodSublimit = location.requirements.floodSublimit;
          }
        }

        let earthquakeSublimit = null;
        if (location.requirements && Object.keys(location.requirements).length > 0) {
          if (!location.requirements.hasOwnProperty('earthquakeSublimit') || location.requirements.earthquakeSublimit === null) {
            earthquakeSublimit = 'Full Sublimit';
          } else {
            earthquakeSublimit = location.requirements.earthquakeSublimit;
          }
        }

        let coastDistance = '';
        const rawCoastDistance = location.details?.coastDistance;
        if (typeof rawCoastDistance === 'string') {
          coastDistance = rawCoastDistance;
        } else if (!isNaN(rawCoastDistance)) {
          coastDistance = rawCoastDistance.toFixed(2) + ' miles';
        }

        return {
          locationId,
          name: location.name,
          address: location.address,
          riskId,
          avg_declared_inventory: declaredInventory || location.coveredValue,
          ...payoutLimits,
          hailDeductible,
          originalHailDeductible: hailDeductible,
          overrideHailDeductible: !!hailDeductibleOverride,
          details: location.details || {},
          floodSublimit,
          earthquakeSublimit,
          swapNeeded: location.requirements?.swapNeeded || false,
          coastDistance,
        };
      }) || [];

    return baseRowData;
  };

  useEffect(() => {
    if (MCEDetails) {
      setLocationsData(mapRowData(sourceLocations));
    }
  }, [sourceLocations, MCEDetails]);

  return (
    <div className="grid-container" style={{ minHeight: '500px' }}>
      <Grid data={locationsData} columns={columnDefs} floatingFilter={false} setGridReference={setGridReference} shouldShowPagination={false} />
    </div>
  );
});
